import React, { useEffect, useState } from "react";
import { Modal, Button, Tooltip } from "antd";
import { CloseOutlined, PieChartOutlined, FileDoneOutlined, FileSyncOutlined } from "@ant-design/icons";

import InsuranceBreakdownMasterModal from "./Breakdown/InsuranceBreakdownMasterModal";
import InsuranceEstimatesMasterModal from "./Estimate/InsuranceEstimatesMasterModal";
import ClaimFollowupsPane from "./Claim/ClaimFollowupsPane";
import { controller } from "../../../controller";
import "./InsuranceMasterModal.css";

/**
 * Single "Insurance" modal that hosts the Breakdowns, Estimates and Claims
 * experiences as three tabs. Owns the shared <Modal> shell + header (patient
 * name, segmented tab switch, Print PDF on the Breakdowns tab, Close). The two
 * existing master modals render in `embedded` mode (no own <Modal>, no in-pane
 * Close/Print bar) as the active tab's body; the Claims tab is a pane built for
 * this shell. Only the active tab is mounted, so its data fetches and the
 * breakdown ResizeObserver don't run for a hidden pane.
 */
export default function InsuranceMasterModal({
  open,
  setOpen,
  activeTab, // "breakdown" | "estimates" | "claims" — see requestTypes.INSURANCE_TABS
  setActiveTab,
  patient,
  // The patient's OWN office (threaded from the row/task). Used for office-scoped
  // credential reads (Show login, provider-account picker) so an FTC/super-admin
  // acting on a cross-office patient uses that patient's office, not the header
  // selectedOffice. Falls back to selectedOffice downstream when not provided.
  officeId,
  // True while policies are being auto-fetched from the PMS (no-policy patient);
  // the sub-modals show a spinner in their Select-Policy sidebar until done.
  isFetchingPolicies,
  patientPolicies,
  // Pre-select this policy in the active tab's Select Policy dropdown on open.
  defaultSelectedPolicyId,
  // Pre-select this specific breakdown/estimate entry in the active tab's
  // left-hand list on open (deep-link from the row "View" buttons).
  defaultSelectedEntryId,
  // The agent's Details Needed suggestion ({ note, fields }) for the Breakdown tab.
  outcomeSuggestion,
  agentType,
  handleScraperAgentCall,
  isAgentScrappingData,
  onPoliciesChanged,
  onNoteSaved,
  onRequestAddPolicy,
  // Hide the machinery for OBTAINING a verification, leaving the results.
  // Set by the Insurance Requests page, which opens this modal purely as a
  // viewer for an office admin: the carrier contact bar, the office's
  // claim-submission identity, the saved portal login, and the
  // request / add-manually / upload-PDF action rail are all tools for whoever
  // *performs* the verification, and mean nothing to the person who asked for
  // it. Every other surface leaves this false and is unaffected.
  hideVerificationTools = false,
  // Breakdown-specific
  breakdownData,
  setLastUploadedBreakdown,
  isGettingDoctorNote,
  checkPatientInsuranceEligibility,
  breakdownEligibilityRefreshTick,
  breakdownModalEligibilityActive,
  setAgentCancelProcess,
  eligibilityAgent2FAStatus,
  eligibilityTaskStatus,
  // Estimates-specific
  predeterminationData,
  defaultTreatmentPlanId,
}) {
  // The office's claim-submission identity + contact block. Fetched HERE rather
  // than in the tab bodies because only the active tab is mounted, so fetching
  // in the shell means one request per modal open instead of one per tab switch.
  // Both tabs pass it to CarrierContactBar (the IDs) and PolicyInformationCollapse
  // (the office contact block).
  const [officeIdentity, setOfficeIdentity] = useState(null);

  // The carrier list behind Policy Information's editable Carrier select, for
  // the same reason: one request per open, shared by all three tabs, where the
  // Breakdowns and Estimates tabs each used to fetch their own on every switch.
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);

  useEffect(
    function () {
      if (!open) return undefined;
      // Same fallback as CarrierPortalCredential: prefer the patient's own office
      // (cross-office FTC case), else the header office.
      const resolvedOfficeId = officeId || localStorage.getItem("selectedOffice");
      if (!resolvedOfficeId) {
        setOfficeIdentity(null);
        return undefined;
      }
      let cancelled = false;
      controller
        .getOfficeInsuranceIdentity(resolvedOfficeId)
        .then(function (data) {
          if (!cancelled) setOfficeIdentity(data || null);
        });
      return function () {
        cancelled = true;
      };
    },
    [open, officeId],
  );

  useEffect(
    function () {
      if (!open) return undefined;
      let cancelled = false;
      setIsLoadingCarriers(true);
      controller
        .listInsuranceProviders()
        .then(function (res) {
          if (cancelled || !res || !Array.isArray(res.data)) return;
          setCarrierOptions(
            res.data
              .filter(function (c) { return c.is_active; })
              .map(function (c) { return { value: c.code, label: c.name }; }),
          );
        })
        .catch(function (err) {
          console.error("Failed to load carriers", err);
        })
        .finally(function () {
          if (!cancelled) setIsLoadingCarriers(false);
        });
      return function () {
        cancelled = true;
      };
    },
    [open],
  );

  const patientName = patient
    ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim()
    : "";

  function handleTabChange(value) {
    setActiveTab(value);
  }

  const header = (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 18px",
        borderBottom: "1px solid #f0f0f0",
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
        <span style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#9aa0b4" }}>
          Insurance
        </span>
        <span style={{ fontSize: 16, fontWeight: 600, color: "#2b2d3a" }}>
          {patientName || "Patient"}
        </span>
      </div>

      <div className="insurance-tab-switch" role="tablist" aria-label="Insurance view" data-onboarding-id="insurance-modal-tabs">
        <button
          type="button"
          role="tab"
          data-onboarding-id="insurance-tab-breakdowns"
          aria-selected={activeTab === "breakdown"}
          className={`insurance-tab${activeTab === "breakdown" ? " is-active" : ""}`}
          onClick={() => handleTabChange("breakdown")}
        >
          <PieChartOutlined />
          Breakdowns
        </button>
        <button
          type="button"
          role="tab"
          data-onboarding-id="insurance-tab-estimates"
          aria-selected={activeTab === "estimates"}
          className={`insurance-tab${activeTab === "estimates" ? " is-active" : ""}`}
          onClick={() => handleTabChange("estimates")}
        >
          <FileDoneOutlined />
          Estimates
        </button>
        <button
          type="button"
          role="tab"
          data-onboarding-id="insurance-tab-claims"
          aria-selected={activeTab === "claims"}
          className={`insurance-tab${activeTab === "claims" ? " is-active" : ""}`}
          onClick={() => handleTabChange("claims")}
        >
          <FileSyncOutlined />
          Claims
        </button>
      </div>

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        <Tooltip title="Close">
          <Button type="text" data-onboarding-id="insurance-modal-close" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
        </Tooltip>
      </div>
    </div>
  );

  return (
    // NOTE: deliberately NO explicit zIndex. antd auto-manages it (~1000) and
    // auto-stacks nested modals (the upload / recalculate / set-default dialogs
    // rendered inside the embedded tab bodies) ABOVE it. A hard-coded zIndex
    // here disables that nested-stacking and left the "add estimate" upload
    // modal stuck behind the main modal.
    <Modal
      open={open}
      width={1680}
      style={{ top: 12, padding: 0, paddingBottom: 0 }}
      bodyStyle={{
        padding: 0,
        height: "115vh",
        maxHeight: "115vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      footer={null}
      closeIcon={false}
      maskClosable={true}
      onCancel={() => setOpen(false)}
      destroyOnClose
    >
      {header}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {activeTab === "claims" ? (
          <ClaimFollowupsPane
            patient={patient}
            officeId={officeId}
            officeIdentity={hideVerificationTools ? null : officeIdentity}
            hideVerificationTools={hideVerificationTools}
            patientPolicies={patientPolicies}
            defaultSelectedPolicyId={defaultSelectedPolicyId}
            isFetchingPolicies={isFetchingPolicies}
            carrierOptions={carrierOptions}
            isLoadingCarriers={isLoadingCarriers}
            onPoliciesChanged={onPoliciesChanged}
            onNoteSaved={onNoteSaved}
            onRequestAddPolicy={onRequestAddPolicy}
          />
        ) : activeTab === "estimates" ? (
          <InsuranceEstimatesMasterModal
            embedded
            showInsuranceEstimatesModal={open}
            setShowInsuranceEstimatesModal={setOpen}
            patientPolicies={patientPolicies}
            defaultSelectedPolicyId={defaultSelectedPolicyId}
            defaultSelectedEntryId={defaultSelectedEntryId}
            outcomeSuggestion={outcomeSuggestion}
            patient={patient}
            officeId={officeId}
            officeIdentity={hideVerificationTools ? null : officeIdentity}
            hideVerificationTools={hideVerificationTools}
            isFetchingPolicies={isFetchingPolicies}
            carrierOptions={carrierOptions}
            isLoadingCarriers={isLoadingCarriers}
            defaultTreatmentPlanId={defaultTreatmentPlanId}
            predeterminationData={predeterminationData}
            agentType={agentType}
            handleScraperAgentCall={handleScraperAgentCall}
            checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
            isAgentScrappingData={isAgentScrappingData}
            onPoliciesChanged={onPoliciesChanged}
            onNoteSaved={onNoteSaved}
            onRequestAddPolicy={onRequestAddPolicy}
          />
        ) : (
          <InsuranceBreakdownMasterModal
            embedded
            showInsuranceBreakdownModal={open}
            setShowInsuranceBreakdownModal={setOpen}
            patientPolicies={patientPolicies}
            defaultSelectedPolicyId={defaultSelectedPolicyId}
            defaultSelectedEntryId={defaultSelectedEntryId}
            outcomeSuggestion={outcomeSuggestion}
            patient={patient}
            officeId={officeId}
            officeIdentity={hideVerificationTools ? null : officeIdentity}
            hideVerificationTools={hideVerificationTools}
            isFetchingPolicies={isFetchingPolicies}
            carrierOptions={carrierOptions}
            isLoadingCarriers={isLoadingCarriers}
            eligibilityData={breakdownData}
            agentType={agentType}
            setLastUploadedBreakdown={setLastUploadedBreakdown}
            isGettingDoctorNote={isGettingDoctorNote}
            handleScraperAgentCall={handleScraperAgentCall}
            isAgentScrappingData={isAgentScrappingData}
            checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
            breakdownEligibilityRefreshTick={breakdownEligibilityRefreshTick}
            breakdownModalEligibilityActive={breakdownModalEligibilityActive}
            setAgentCancelProcess={setAgentCancelProcess}
            eligibilityAgent2FAStatus={eligibilityAgent2FAStatus}
            eligibilityTaskStatus={eligibilityTaskStatus}
            onPoliciesChanged={onPoliciesChanged}
            onNoteSaved={onNoteSaved}
            onRequestAddPolicy={onRequestAddPolicy}
          />
        )}
      </div>
    </Modal>
  );
}
